import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SystemService } from '@shared/service/system/system.service';

export interface DropdownItem {
  name: string;
  url: string;
  icon?: string;
}

@Directive({
  selector: '[appSidebarTooltip]',
  standalone: true,
})
export class SidebarTooltipDirective implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private router = inject(Router);

  @Input('appSidebarTooltip') tooltipText = '';
  @Input() showTooltip = true;
  @Input() isDropdown = false;
  @Input() dropdownItems: DropdownItem[] = [];
  @Input() systemService!: SystemService;

  private tooltip: HTMLElement | null = null;
  private mouseEnterListener?: () => void;
  private mouseLeaveListener?: () => void;
  private isMouseOverTooltip = false;

  isHebrew(): boolean {
    return this.systemService.direction() === 'rtl';
  }

  ngOnInit(): void {
    if (!this.showTooltip) return;

    this.mouseEnterListener = () => this.showTooltipElement();
    this.mouseLeaveListener = () => {
      // For dropdown tooltips, delay hiding to allow mouse to move to tooltip
      if (this.isDropdown) {
        setTimeout(() => {
          // Only hide if mouse is not over tooltip
          if (this.tooltip && !this.isMouseOverTooltip) {
            this.hideTooltipElement();
          }
        }, 100);
      } else {
        this.hideTooltipElement();
      }
    };

    this.renderer.listen(this.elementRef.nativeElement, 'mouseenter', this.mouseEnterListener);
    this.renderer.listen(this.elementRef.nativeElement, 'mouseleave', this.mouseLeaveListener);
  }

  ngOnDestroy(): void {
    this.hideTooltipElement();
  }

  private showTooltipElement(): void {
    if (!this.tooltipText || this.tooltip) return;

    this.tooltip = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltip, 'sidebar-tooltip');

    if (this.isDropdown && this.dropdownItems.length > 0) {
      // Create dropdown-style tooltip
      const header = this.renderer.createElement('div');
      this.renderer.addClass(header, 'tooltip-dropdown-header');
      this.renderer.setProperty(header, 'textContent', this.tooltipText);
      this.renderer.appendChild(this.tooltip, header);

      // Create list of items
      const list = this.renderer.createElement('ul');
      this.renderer.addClass(list, 'sidebar-tooltip-list');

      this.dropdownItems.forEach((item) => {
        const isActive = this.router.url.startsWith(item.url);
        const listItem = this.renderer.createElement('li');
        this.renderer.addClass(listItem, 'sidebar-tooltip-list-item');

        if (isActive) {
          this.renderer.addClass(listItem, 'sidebar-tooltip-list-item-active');
        } else {
          this.renderer.addClass(listItem, 'sidebar-tooltip-list-item-not-active');
        }

        // Add hover effect (only if not active)
        this.renderer.listen(listItem, 'mouseenter', () => {
          if (!isActive) {
            this.renderer.setStyle(listItem, 'background-color', 'var(--cui-gray-100, #f3f4f7)');
          }
        });

        this.renderer.listen(listItem, 'mouseleave', () => {
          if (!isActive) {
            this.renderer.setStyle(listItem, 'background-color', 'transparent');
          }
        });

        // Add click handler to navigate
        this.renderer.listen(listItem, 'click', () => {
          this.router.navigate([item.url]);
          this.hideTooltipElement();
        });

        const row_ = this.renderer.createElement('c-row');
        this.renderer.setStyle(row_, 'display', 'flex');

        const icon_ = this.renderer.createElement('i');
        this.renderer.addClass(icon_, 'sidebar-tooltip-list-item-icon');
        (item.icon || 'fa fa-asterisk').split(' ').forEach((className) => {
          this.renderer.addClass(icon_, className.trim());
        });

        const colLeft = this.renderer.createElement('c-col');
        this.renderer.addClass(colLeft, 'me-3');
        this.renderer.appendChild(colLeft, icon_);
        this.renderer.appendChild(row_, colLeft);

        const colRight = this.renderer.createElement('c-col');
        this.renderer.setProperty(colRight, 'textContent', item.name);
        this.renderer.addClass(colRight, 'sidebar-tooltip-list-item-text');

        this.renderer.appendChild(row_, colRight);
        this.renderer.appendChild(listItem, row_);

        this.renderer.appendChild(list, listItem);
      });

      this.renderer.appendChild(this.tooltip, list);
    } else {
      // Regular tooltip
      this.renderer.setProperty(this.tooltip, 'textContent', this.tooltipText);
    }

    // Position tooltip
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.renderer.setStyle(this.tooltip, 'position', 'fixed');
    this.renderer.setStyle(this.tooltip, 'left', `${rect.right + 8}px`);
    this.renderer.setStyle(this.tooltip, 'top', `${rect.top + rect.height / 2}px`);
    this.renderer.setStyle(this.tooltip, 'transform', 'translateY(-50%)');
    this.renderer.setStyle(this.tooltip, 'background-color', 'var(--cui-white, #ffffff)');
    this.renderer.setStyle(
      this.tooltip,
      'color',
      '#737373', // Match old manager-app color
    );
    this.renderer.setStyle(this.tooltip, 'padding', this.isDropdown ? '12px 0' : '12px 16px');
    this.renderer.setStyle(this.tooltip, 'border-radius', '4px');
    this.renderer.setStyle(this.tooltip, 'font-size', '13px');
    this.renderer.setStyle(
      this.tooltip,
      'font-family',
      'Ploni, OpenSansHebrew, "Varela Round", "Helvetica Neue", Helvetica, Arial, sans-serif',
    );
    this.renderer.setStyle(this.tooltip, 'white-space', 'nowrap');
    this.renderer.setStyle(this.tooltip, 'z-index', '1050');
    // Allow pointer events for dropdown tooltips to enable clicking
    this.renderer.setStyle(this.tooltip, 'pointer-events', this.isDropdown ? 'auto' : 'none');
    this.renderer.setStyle(this.tooltip, 'opacity', '0');
    this.renderer.setStyle(this.tooltip, 'transition', 'opacity 0.2s ease-in-out');
    this.renderer.setStyle(this.tooltip, 'box-shadow', '0 2px 8px rgba(0, 0, 0, 0.15)');

    // Create arrow using ::before pseudo-element via CSS
    const style = document.createElement('style');
    style.textContent = `
      .sidebar-tooltip::before {
        content: '';
        position: absolute;
        left: -6px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 6px 6px 6px 0;
        border-color: transparent var(--cui-white, #ffffff) transparent transparent;
      }
    `;
    document.head.appendChild(style);

    this.renderer.appendChild(document.body, this.tooltip);

    // For dropdown tooltips, add mouse enter/leave listeners to keep tooltip visible
    if (this.isDropdown) {
      this.renderer.listen(this.tooltip, 'mouseenter', () => {
        this.isMouseOverTooltip = true;
      });

      this.renderer.listen(this.tooltip, 'mouseleave', () => {
        this.isMouseOverTooltip = false;
        this.hideTooltipElement();
      });
    }

    // Fade in
    setTimeout(() => {
      if (this.tooltip) {
        this.renderer.setStyle(this.tooltip, 'opacity', '1');
      }
    }, 10);
  }

  private hideTooltipElement(): void {
    if (this.tooltip) {
      this.renderer.removeChild(document.body, this.tooltip);
      this.tooltip = null;
      this.isMouseOverTooltip = false;
    }
  }
}
